import { useLayoutEffect, useRef } from 'react';

const reduced = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/**
 * 자리가 바뀐 항목을 이전 자리에서 새 자리로 미끄러지듯 옮긴다 (FLIP).
 * 컨테이너 안의 [data-flip-id] 요소 위치를 렌더마다 기억해 두었다가, 다음 렌더에서 달라졌으면 그만큼 거꾸로 옮긴 뒤 되돌린다.
 * 처음 그릴 때는 움직이지 않고, 그 뒤 새로 생긴 항목은 살짝 떠오르며 나타난다.
 * 위치는 컨테이너 기준이라 페이지 스크롤로는 움직이지 않는다.
 */
const useFlip = () => {
  const ref = useRef(null);
  const prev = useRef(null);

  useLayoutEffect(() => {
    const box = ref.current;
    if (!box) return;
    const origin = box.getBoundingClientRect();
    const next = new Map();
    box.querySelectorAll('[data-flip-id]').forEach((el) => {
      const r = el.getBoundingClientRect();
      const pos = {
        x: r.left - origin.left + box.scrollLeft,
        y: r.top - origin.top + box.scrollTop,
      };
      const id = el.dataset.flipId;
      next.set(id, pos);
      if (!prev.current || reduced() || !el.animate) return;
      const before = prev.current.get(id);
      if (before) {
        const dx = before.x - pos.x;
        const dy = before.y - pos.y;
        // 스크롤바가 생기고 없어지며 열 폭이 몇 px 바뀌는 정도는 그냥 둔다 (흔들려 보인다)
        if (Math.abs(dx) > 6 || Math.abs(dy) > 6) {
          el.animate([{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'none' }], {
            duration: 320,
            easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)',
          });
        }
      } else {
        el.animate(
          [
            { opacity: 0, transform: 'translateY(8px) scale(0.98)' },
            { opacity: 1, transform: 'none' },
          ],
          { duration: 260, easing: 'ease-out' },
        );
      }
    });
    prev.current = next;
  });

  return ref;
};

export default useFlip;
